/**
 * AuthAdapter — the seam between the app and the organisation's identity provider.
 *
 * This template ships NO production authentication. Two adapters exist:
 *  - MockAuthAdapter (VITE_DATA_MODE=mock): a fictional demo user whose role can be switched in the
 *    sidebar to preview permissions. No passwords, no tokens.
 *  - ApiAuthAdapter (VITE_DATA_MODE=api): the Easy SaaS Tracker API's cookie session. `POST /auth/login`
 *    sets an httpOnly cookie that the browser sends with every request (`credentials: 'include'`), so the
 *    frontend never sees or stores a token. `GET /me` says who is signed in. Never store tokens in localStorage.
 */
import type { Role, User } from '@/types/models';
import { permissionsFor } from './permissions';
import { DataError } from '@/services/DataProvider';
import { HttpClient } from '@/providers/api/httpClient';

export interface AuthAdapter {
  readonly kind: 'mock' | 'api';
  /** The signed-in user, or null when nobody is signed in. */
  getCurrentUser(): Promise<User | null>;
  /** Access token for API calls (null when the backend uses a cookie session). */
  getAccessToken(): Promise<string | null>;
  /** Start sign-in (usually a redirect to the identity provider). */
  logIn(): Promise<void>;
  logOut(): Promise<void>;
  /** Email ID + password Log In (cookie sessions); used by the Log In page. */
  logInWithPassword?(email: string, password: string, remember: boolean): Promise<User>;
  /** Demo only: switch the preview role. */
  setDemoRole?(role: Role): Promise<User>;
}

/** Path of the Log In page (src/auth/LoginPage.tsx). */
export const LOGIN_PATH = '/login';

/** Where the browser goes; replaceable in tests. */
export interface Navigator {
  assign(url: string): void;
}

export const DEMO_ROLE_KEY = 'easysaas.demo.role';
const ROLES: Role[] = ['admin', 'management', 'manager', 'employee'];

const readRole = (): Role => {
  try {
    const r = localStorage.getItem(DEMO_ROLE_KEY) as Role | null;
    return r && ROLES.includes(r) ? r : 'admin';
  } catch {
    return 'admin';
  }
};

export class MockAuthAdapter implements AuthAdapter {
  readonly kind = 'mock' as const;
  private signedIn = true;
  private role: Role;
  constructor(role?: Role) {
    this.role = role || readRole();
  }
  private user(): User {
    return { id: 'demo-user', name: 'Demo User', email: 'demo.user@example.com', roles: [this.role], permissions: permissionsFor([this.role]) };
  }
  async getCurrentUser() {
    return this.signedIn ? this.user() : null;
  }
  async getAccessToken() {
    return null;
  }
  async logIn() {
    this.signedIn = true;
  }
  async logOut() {
    this.signedIn = false;
  }
  async setDemoRole(role: Role) {
    this.role = role;
    try {
      localStorage.setItem(DEMO_ROLE_KEY, role);
    } catch {
      /* preference only */
    }
    return this.user();
  }
}

export class ApiAuthAdapter implements AuthAdapter {
  readonly kind = 'api' as const;
  private readonly http: HttpClient;
  private readonly nav: Navigator;
  constructor(baseUrl: string, opts: { fetchImpl?: typeof fetch; nav?: Navigator } = {}) {
    this.http = new HttpClient({ baseUrl, getToken: () => this.getAccessToken(), fetchImpl: opts.fetchImpl });
    this.nav = opts.nav ?? { assign: (url) => window.location.assign(url) };
  }
  async getCurrentUser(): Promise<User | null> {
    try {
      return await this.http.get<User>('/me');
    } catch (e) {
      if (e instanceof DataError && (e.status === 401 || e.status === 403)) return null;
      throw e;
    }
  }
  /** Cookie session: the browser sends the httpOnly cookie itself, so there is no token to hand out. */
  async getAccessToken(): Promise<string | null> {
    return null;
  }
  /** Opens the Log In page, which returns to the current page afterwards. */
  async logIn(): Promise<void> {
    const here = window.location.pathname + window.location.search;
    this.nav.assign(`${LOGIN_PATH}?next=${encodeURIComponent(here)}`);
  }
  /** `POST /auth/login` — on success the API sets the session cookie and returns the user. */
  logInWithPassword(email: string, password: string, remember: boolean): Promise<User> {
    return this.http.post<User>('/auth/login', { email, password, remember });
  }
  /** Ends the session on the server (clears the cookie), then shows the Log In page. */
  async logOut(): Promise<void> {
    await this.http.post<void>('/auth/logout');
    this.nav.assign(LOGIN_PATH);
  }
}
